import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormGroup from "@material-ui/core/FormGroup";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableContainer from "@material-ui/core/TableContainer";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import SaveIcon from "@material-ui/icons/Save";
import SettingsIcon from "@material-ui/icons/Settings";
import EmailIcon from "@material-ui/icons/Email";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import TimerIcon from "@material-ui/icons/Timer";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventIcon from "@material-ui/icons/Event";
import StarIcon from "@material-ui/icons/Star";
import LabelIcon from "@material-ui/icons/Label";
import CategoryIcon from "@material-ui/icons/Category";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(2),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	tabContent: {
		padding: theme.spacing(3),
	},
	sectionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(1),
		color: theme.palette.primary.main,
	},
	sectionDesc: {
		marginBottom: theme.spacing(3),
	},
	holidayPast: {
		opacity: 0.5,
	},
}));

const TabPanel = ({ children, value, index }) => (
	<div role="tabpanel" hidden={value !== index}>
		{value === index && <Box>{children}</Box>}
	</div>
);

const DAY_OPTIONS = [
	{ value: 0, label: "Domingo" },
	{ value: 1, label: "Lunes" },
	{ value: 2, label: "Martes" },
	{ value: 3, label: "Miercoles" },
	{ value: 4, label: "Jueves" },
	{ value: 5, label: "Viernes" },
	{ value: 6, label: "Sabado" },
];

const Settings = () => {
	const classes = useStyles();
	const [tab, setTab] = useState(0);
	const [settings, setSettings] = useState([]);
	const [newHolidayDate, setNewHolidayDate] = useState("");
	const [newHolidayName, setNewHolidayName] = useState("");
	const [editingHoliday, setEditingHoliday] = useState(null);
	const [editDate, setEditDate] = useState("");
	const [editName, setEditName] = useState("");
	const [tags, setTags] = useState([]);
	const [newTagName, setNewTagName] = useState("");
	const [newTagColor, setNewTagColor] = useState("#1976d2");
	const [editingTag, setEditingTag] = useState(null);
	const [editTagName, setEditTagName] = useState("");
	const [editTagColor, setEditTagColor] = useState("");
	const [deleteTagConfirm, setDeleteTagConfirm] = useState(false);
	const [deletingTagId, setDeletingTagId] = useState(null);
	const [newCategory, setNewCategory] = useState("");
	const [editingCatIdx, setEditingCatIdx] = useState(null);
	const [editCatName, setEditCatName] = useState("");
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [deletingHolidayDate, setDeletingHolidayDate] = useState(null);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();
		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex >= 0) {
						aux[settingIndex].value = data.setting.value;
					}
					return aux;
				});
			}
		});
		return () => { socket.disconnect(); };
	}, []);

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : "";
	};

	const updateLocalSetting = (key, value) => {
		setSettings(prev => {
			const aux = [...prev];
			const idx = aux.findIndex(s => s.key === key);
			if (idx >= 0) {
				aux[idx].value = value;
			} else {
				aux.push({ key, value, createdAt: new Date(), updatedAt: new Date() });
			}
			return aux;
		});
	};

	const saveMultiple = async (keys) => {
		try {
			for (const key of keys) {
				await api.put(`/settings/${key}`, { value: getSettingValue(key) });
			}
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSelectedDays = () => {
		const val = getSettingValue("businessHoursDays");
		if (!val) return [];
		return val.split(",").map(d => parseInt(d.trim(), 10)).filter(d => !isNaN(d));
	};

	const toggleDay = (day) => {
		const current = getSelectedDays();
		const newDays = current.includes(day)
			? current.filter(d => d !== day)
			: [...current, day].sort();
		updateLocalSetting("businessHoursDays", newDays.join(","));
	};

	// Quick answer categories helpers
	const getCategories = () => {
		try {
			const val = getSettingValue("quickAnswerCategories");
			return val ? JSON.parse(val) : [];
		} catch { return []; }
	};

	const setCategories = (cats) => {
		updateLocalSetting("quickAnswerCategories", JSON.stringify(cats));
	};

	const addCategory = () => {
		if (!newCategory.trim()) { toast.error("Ingrese un nombre."); return; }
		const cats = getCategories();
		if (cats.includes(newCategory.trim())) { toast.error("Ya existe."); return; }
		cats.push(newCategory.trim());
		setCategories(cats);
		setNewCategory("");
	};

	const removeCategory = (idx) => {
		const cats = getCategories().filter((_, i) => i !== idx);
		setCategories(cats);
	};

	const saveEditCategory = () => {
		if (!editCatName.trim()) return;
		const cats = getCategories();
		cats[editingCatIdx] = editCatName.trim();
		setCategories(cats);
		setEditingCatIdx(null);
		setEditCatName("");
	};

	// Tags helpers
	useEffect(() => {
		const fetchTags = async () => {
			try {
				const { data } = await api.get("/tags");
				setTags(data);
			} catch (err) { toastError(err); }
		};
		fetchTags();
	}, []);

	const handleAddTag = async () => {
		if (!newTagName.trim()) { toast.error("Ingrese un nombre para la etiqueta."); return; }
		try {
			const { data } = await api.post("/tags", { name: newTagName.trim(), color: newTagColor });
			setTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
			setNewTagName("");
			setNewTagColor("#1976d2");
			toast.success("Etiqueta creada.");
		} catch (err) { toastError(err); }
	};

	const handleUpdateTag = async () => {
		if (!editTagName.trim()) { toast.error("Ingrese un nombre."); return; }
		try {
			const { data } = await api.put(`/tags/${editingTag}`, { name: editTagName.trim(), color: editTagColor });
			setTags(prev => prev.map(t => t.id === editingTag ? data : t));
			setEditingTag(null);
			toast.success("Etiqueta actualizada.");
		} catch (err) { toastError(err); }
	};

	const handleDeleteTag = async () => {
		try {
			await api.delete(`/tags/${deletingTagId}`);
			setTags(prev => prev.filter(t => t.id !== deletingTagId));
			setDeleteTagConfirm(false);
			setDeletingTagId(null);
			toast.success("Etiqueta eliminada.");
		} catch (err) { toastError(err); }
	};

	// Holidays helpers
	const getHolidays = () => {
		try {
			const val = getSettingValue("holidays");
			return val ? JSON.parse(val) : [];
		} catch { return []; }
	};

	const setHolidays = (holidays) => {
		updateLocalSetting("holidays", JSON.stringify(holidays));
	};

	const addHoliday = () => {
		if (!newHolidayDate || !newHolidayName) {
			toast.error("Ingrese fecha y nombre del festivo.");
			return;
		}
		const holidays = getHolidays();
		if (holidays.find(h => h.date === newHolidayDate)) {
			toast.error("Ya existe un festivo en esa fecha.");
			return;
		}
		holidays.push({ date: newHolidayDate, name: newHolidayName });
		holidays.sort((a, b) => a.date.localeCompare(b.date));
		setHolidays(holidays);
		setNewHolidayDate("");
		setNewHolidayName("");
	};

	const confirmRemoveHoliday = (date) => {
		setDeletingHolidayDate(date);
		setDeleteConfirmOpen(true);
	};

	const removeHoliday = () => {
		const holidays = getHolidays().filter(h => h.date !== deletingHolidayDate);
		setHolidays(holidays);
		setDeleteConfirmOpen(false);
		setDeletingHolidayDate(null);
	};

	const startEditHoliday = (holiday) => {
		setEditingHoliday(holiday.date);
		setEditDate(holiday.date);
		setEditName(holiday.name);
	};

	const cancelEditHoliday = () => {
		setEditingHoliday(null);
		setEditDate("");
		setEditName("");
	};

	const saveEditHoliday = () => {
		if (!editDate || !editName) {
			toast.error("Ingrese fecha y nombre del festivo.");
			return;
		}
		const holidays = getHolidays().map(h =>
			h.date === editingHoliday ? { date: editDate, name: editName } : h
		);
		holidays.sort((a, b) => a.date.localeCompare(b.date));
		setHolidays(holidays);
		setEditingHoliday(null);
		setEditDate("");
		setEditName("");
	};

	const formatHolidayDate = (dateStr) => {
		try {
			const [y, m, d] = dateStr.split("-");
			const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
			const days = ["Domingo","Lunes","Martes","Miercoles","Jueves","Viernes","Sabado"];
			const dt = new Date(dateStr + "T12:00:00");
			return `${days[dt.getDay()]} ${parseInt(d)} ${months[parseInt(m)-1]} ${y}`;
		} catch { return dateStr; }
	};

	const isHolidayPast = (dateStr) => {
		const today = new Date().toISOString().split("T")[0];
		return dateStr < today;
	};

	return (
		<MainContainer>
			<MainHeader>
				<Title>{i18n.t("settings.title")}</Title>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Tabs
					value={tab}
					onChange={(e, v) => setTab(v)}
					indicatorColor="primary"
					textColor="primary"
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab icon={<SettingsIcon />} label="General" />
					<Tab icon={<EmailIcon />} label="Correo SMTP" />
					<Tab icon={<TimerIcon />} label="Cierre Automatico" />
					<Tab icon={<ScheduleIcon />} label="Horario Atencion" />
					<Tab icon={<EventIcon />} label="Festivos" />
					<Tab icon={<StarIcon />} label="Encuesta" />
					<Tab icon={<LabelIcon />} label="Etiquetas" />
					<Tab icon={<CategoryIcon />} label="Categorias R.R." />
					<Tab icon={<VpnKeyIcon />} label="API" />
				</Tabs>

				{/* Tab General */}
				<TabPanel value={tab} index={0}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Configuracion General
						</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Ajustes generales de la plataforma.
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12} md={6}>
								<TextField label="Nombre de la plataforma" variant="outlined" fullWidth
									value={settings.length > 0 ? getSettingValue("appName") : ""}
									onChange={(e) => updateLocalSetting("appName", e.target.value)} />
							</Grid>
							<Grid item xs={12} md={6}>
								<Typography variant="body1" style={{ marginBottom: 8 }}>
									{i18n.t("settings.settings.userCreation.name")}
								</Typography>
								<Select variant="outlined" native fullWidth
									value={settings.length > 0 ? getSettingValue("userCreation") : ""}
									onChange={(e) => updateLocalSetting("userCreation", e.target.value)}>
									<option value="enabled">{i18n.t("settings.settings.userCreation.options.enabled")}</option>
									<option value="disabled">{i18n.t("settings.settings.userCreation.options.disabled")}</option>
								</Select>
							</Grid>
							<Grid item xs={12}>
								<Button variant="contained" color="primary" startIcon={<SaveIcon />}
									onClick={() => saveMultiple(["appName", "userCreation"])}>Guardar</Button>
							</Grid>
						</Grid>
					</Paper>
				</TabPanel>

				{/* Tab SMTP */}
				<TabPanel value={tab} index={1}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Configuracion SMTP</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Configure el servidor de correo para notificaciones y recuperacion de contraseña.
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12} md={8}>
								<TextField label="Servidor SMTP" placeholder="smtp.gmail.com" variant="outlined" fullWidth
									value={settings.length > 0 ? getSettingValue("smtpHost") : ""}
									onChange={(e) => updateLocalSetting("smtpHost", e.target.value)} />
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField label="Puerto" placeholder="587" variant="outlined" fullWidth
									value={settings.length > 0 ? getSettingValue("smtpPort") : ""}
									onChange={(e) => updateLocalSetting("smtpPort", e.target.value)} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField label="Usuario SMTP" placeholder="usuario@gmail.com" variant="outlined" fullWidth
									value={settings.length > 0 ? getSettingValue("smtpUser") : ""}
									onChange={(e) => updateLocalSetting("smtpUser", e.target.value)} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField label="Contraseña SMTP" type="password" variant="outlined" fullWidth
									value={settings.length > 0 ? getSettingValue("smtpPass") : ""}
									onChange={(e) => updateLocalSetting("smtpPass", e.target.value)} />
							</Grid>
							<Grid item xs={12}>
								<TextField label="Email remitente" placeholder="noreply@miempresa.com" variant="outlined" fullWidth
									value={settings.length > 0 ? getSettingValue("smtpFrom") : ""}
									onChange={(e) => updateLocalSetting("smtpFrom", e.target.value)} />
							</Grid>
							<Grid item xs={12}>
								<Button variant="contained" color="primary" startIcon={<SaveIcon />}
									onClick={() => saveMultiple(["smtpHost", "smtpPort", "smtpUser", "smtpPass", "smtpFrom"])}>Guardar SMTP</Button>
							</Grid>
						</Grid>
					</Paper>
				</TabPanel>

				{/* Tab Cierre Automatico */}
				<TabPanel value={tab} index={2}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Cierre Automatico de Chats</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Los chats sin actividad se cerraran automaticamente despues del tiempo configurado.
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControlLabel
									control={<Switch checked={getSettingValue("autoCloseEnabled") === "true"}
										onChange={(e) => updateLocalSetting("autoCloseEnabled", e.target.checked ? "true" : "false")} color="primary" />}
									label={<Typography variant="body1" style={{ fontWeight: 500 }}>Habilitar cierre automatico</Typography>} />
							</Grid>
							{getSettingValue("autoCloseEnabled") === "true" && (
								<>
									<Grid item xs={12} md={4}>
										<TextField label="Horas de inactividad para cerrar" type="number" variant="outlined" fullWidth
											inputProps={{ min: 1, max: 720 }}
											value={settings.length > 0 ? getSettingValue("autoCloseHours") : "24"}
											onChange={(e) => updateLocalSetting("autoCloseHours", e.target.value)}
											helperText="Los chats abiertos o pendientes sin actividad por este numero de horas se cerraran automaticamente." />
									</Grid>
									<Grid item xs={12}>
										<TextField label="Mensaje de cierre automatico" variant="outlined" fullWidth multiline rows={3}
											value={settings.length > 0 ? getSettingValue("autoCloseMessage") : ""}
											onChange={(e) => updateLocalSetting("autoCloseMessage", e.target.value)}
											helperText="Este mensaje se enviara al contacto cuando su chat se cierre automaticamente. Dejelo vacio para cerrar sin enviar mensaje." />
									</Grid>
								</>
							)}
							<Grid item xs={12}>
								<Button variant="contained" color="primary" startIcon={<SaveIcon />}
									onClick={() => saveMultiple(["autoCloseEnabled", "autoCloseHours", "autoCloseMessage"])}>Guardar</Button>
							</Grid>
						</Grid>
					</Paper>
				</TabPanel>

				{/* Tab Horario Atencion */}
				<TabPanel value={tab} index={3}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Horario de Atencion</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Cuando un contacto escribe fuera del horario configurado o en un dia festivo, se le enviara un mensaje automatico.
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControlLabel
									control={<Switch checked={getSettingValue("businessHoursEnabled") === "true"}
										onChange={(e) => updateLocalSetting("businessHoursEnabled", e.target.checked ? "true" : "false")} color="primary" />}
									label={<Typography variant="body1" style={{ fontWeight: 500 }}>Habilitar horario de atencion</Typography>} />
							</Grid>
							{getSettingValue("businessHoursEnabled") === "true" && (
								<>
									<Grid item xs={12} md={3}>
										<TextField label="Hora inicio" type="time" variant="outlined" fullWidth
											InputLabelProps={{ shrink: true }}
											value={settings.length > 0 ? getSettingValue("businessHoursStart") : "08:00"}
											onChange={(e) => updateLocalSetting("businessHoursStart", e.target.value)} />
									</Grid>
									<Grid item xs={12} md={3}>
										<TextField label="Hora fin" type="time" variant="outlined" fullWidth
											InputLabelProps={{ shrink: true }}
											value={settings.length > 0 ? getSettingValue("businessHoursEnd") : "17:00"}
											onChange={(e) => updateLocalSetting("businessHoursEnd", e.target.value)} />
									</Grid>
									<Grid item xs={12} md={6}>
										<Typography variant="body1" style={{ marginBottom: 8 }}>Dias de atencion</Typography>
										<FormGroup row>
											{DAY_OPTIONS.map((day) => (
												<FormControlLabel key={day.value}
													control={<Checkbox checked={getSelectedDays().includes(day.value)}
														onChange={() => toggleDay(day.value)} color="primary" size="small" />}
													label={day.label} />
											))}
										</FormGroup>
									</Grid>
									<Grid item xs={12}>
										<FormControlLabel
											control={<Checkbox checked={getSettingValue("holidaysEnabled") === "true"}
												onChange={(e) => updateLocalSetting("holidaysEnabled", e.target.checked ? "true" : "false")}
												color="primary" />}
											label={<Typography variant="body1" style={{ fontWeight: 500 }}>Incluir dias festivos (los festivos se configuran en el tab "Festivos")</Typography>} />
									</Grid>
									<Grid item xs={12}>
										<TextField label="Mensaje fuera de horario" variant="outlined" fullWidth multiline rows={3}
											value={settings.length > 0 ? getSettingValue("outsideHoursMessage") : ""}
											onChange={(e) => updateLocalSetting("outsideHoursMessage", e.target.value)}
											helperText="Este mensaje se enviara automaticamente cuando un contacto escriba fuera del horario de atencion o en un dia festivo." />
									</Grid>
								</>
							)}
							<Grid item xs={12}>
								<Button variant="contained" color="primary" startIcon={<SaveIcon />}
									onClick={() => saveMultiple(["businessHoursEnabled", "businessHoursStart", "businessHoursEnd", "businessHoursDays", "outsideHoursMessage", "holidaysEnabled"])}>
									Guardar Horario</Button>
							</Grid>
						</Grid>
					</Paper>
				</TabPanel>

				{/* Tab Festivos */}
				<TabPanel value={tab} index={4}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Dias Festivos</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Los dias festivos se consideran como dias no laborales. Si el horario de atencion esta habilitado, los contactos que escriban en un dia festivo recibiran el mensaje fuera de horario.
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControlLabel
									control={<Switch checked={getSettingValue("holidaysEnabled") === "true"}
										onChange={(e) => updateLocalSetting("holidaysEnabled", e.target.checked ? "true" : "false")} color="primary" />}
									label={<Typography variant="body1" style={{ fontWeight: 500 }}>Habilitar dias festivos</Typography>} />
							</Grid>

							{getSettingValue("holidaysEnabled") === "true" && (
								<>
									<Grid item xs={12}>
										<Box display="flex" gap={2} alignItems="center" style={{ gap: 12, marginBottom: 16 }}>
											<TextField label="Fecha" type="date" variant="outlined" size="small"
												InputLabelProps={{ shrink: true }}
												value={newHolidayDate}
												onChange={(e) => setNewHolidayDate(e.target.value)}
												style={{ width: 200 }} />
											<TextField label="Nombre del festivo" variant="outlined" size="small"
												value={newHolidayName}
												onChange={(e) => setNewHolidayName(e.target.value)}
												style={{ flex: 1 }} />
											<Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addHoliday}>
												Agregar
											</Button>
										</Box>
									</Grid>
									<Grid item xs={12}>
										<TableContainer>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell>Fecha</TableCell>
														<TableCell>Dia</TableCell>
														<TableCell>Festivo</TableCell>
														<TableCell>Estado</TableCell>
														<TableCell align="center">Acciones</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{getHolidays().map((holiday) => (
														<TableRow key={holiday.date} className={isHolidayPast(holiday.date) ? classes.holidayPast : ""} hover>
															{editingHoliday === holiday.date ? (
																<>
																	<TableCell>
																		<TextField type="date" size="small" variant="outlined"
																			InputLabelProps={{ shrink: true }}
																			value={editDate} onChange={(e) => setEditDate(e.target.value)}
																			style={{ width: 160 }} />
																	</TableCell>
																	<TableCell>{editDate ? formatHolidayDate(editDate).split(" ")[0] : ""}</TableCell>
																	<TableCell>
																		<TextField size="small" variant="outlined"
																			value={editName} onChange={(e) => setEditName(e.target.value)}
																			style={{ width: "100%" }} />
																	</TableCell>
																	<TableCell></TableCell>
																	<TableCell align="center">
																		<IconButton size="small" onClick={saveEditHoliday} style={{ color: "#2e7d32" }}>
																			<CheckIcon fontSize="small" />
																		</IconButton>
																		<IconButton size="small" onClick={cancelEditHoliday} style={{ color: "#d32f2f" }}>
																			<CloseIcon fontSize="small" />
																		</IconButton>
																	</TableCell>
																</>
															) : (
																<>
																	<TableCell>{holiday.date}</TableCell>
																	<TableCell>{formatHolidayDate(holiday.date).split(" ")[0]}</TableCell>
																	<TableCell>{holiday.name}</TableCell>
																	<TableCell>
																		{isHolidayPast(holiday.date) ? (
																			<Chip label="Pasado" size="small" style={{ backgroundColor: "#e0e0e0" }} />
																		) : (
																			<Chip label="Vigente" size="small" style={{ backgroundColor: "#c8e6c9", color: "#2e7d32" }} />
																		)}
																	</TableCell>
																	<TableCell align="center">
																		<IconButton size="small" onClick={() => startEditHoliday(holiday)}>
																			<EditIcon fontSize="small" />
																		</IconButton>
																		<IconButton size="small" onClick={() => confirmRemoveHoliday(holiday.date)}>
																			<DeleteIcon fontSize="small" />
																		</IconButton>
																	</TableCell>
																</>
															)}
														</TableRow>
													))}
													{getHolidays().length === 0 && (
														<TableRow>
															<TableCell colSpan={5} align="center">No hay festivos configurados</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</TableContainer>
									</Grid>
								</>
							)}

							<Grid item xs={12}>
								<Button variant="contained" color="primary" startIcon={<SaveIcon />}
									onClick={() => saveMultiple(["holidays", "holidaysEnabled"])}>
									Guardar Festivos
								</Button>
							</Grid>
						</Grid>
					</Paper>

					<Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
						<DialogTitle>Eliminar festivo</DialogTitle>
						<DialogContent>
							<DialogContentText>
								¿Esta seguro que desea eliminar este dia festivo?
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setDeleteConfirmOpen(false)}>
								Cancelar
							</Button>
							<Button onClick={removeHoliday} color="secondary" variant="contained">
								Eliminar
							</Button>
						</DialogActions>
					</Dialog>
				</TabPanel>

				{/* Tab Encuesta */}
				<TabPanel value={tab} index={5}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Encuesta de Satisfaccion</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Cuando un chat se cierra, se puede enviar una encuesta de satisfaccion al contacto para que califique la atencion recibida.
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControlLabel
									control={<Switch checked={getSettingValue("surveyEnabled") === "true"}
										onChange={(e) => updateLocalSetting("surveyEnabled", e.target.checked ? "true" : "false")} color="primary" />}
									label={<Typography variant="body1" style={{ fontWeight: 500 }}>Habilitar encuesta de satisfaccion</Typography>} />
							</Grid>
							{getSettingValue("surveyEnabled") === "true" && (
								<Grid item xs={12}>
									<TextField label="Mensaje de encuesta" variant="outlined" fullWidth multiline rows={3}
										value={settings.length > 0 ? getSettingValue("surveyMessage") : ""}
										onChange={(e) => updateLocalSetting("surveyMessage", e.target.value)}
										helperText="Este mensaje se enviara al contacto cuando se cierre su chat. El contacto debe responder con un numero del 1 al 5." />
								</Grid>
							)}
							<Grid item xs={12}>
								<Button variant="contained" color="primary" startIcon={<SaveIcon />}
									onClick={() => saveMultiple(["surveyEnabled", "surveyMessage"])}>
									Guardar Encuesta
								</Button>
							</Grid>
						</Grid>
					</Paper>
				</TabPanel>

				{/* Tab Etiquetas */}
				<TabPanel value={tab} index={6}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Etiquetas</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Las etiquetas permiten clasificar los chats por tema. Puede agregar, editar o eliminar etiquetas.
						</Typography>

						<Box display="flex" style={{ gap: 12, marginBottom: 16 }} alignItems="center">
							<TextField label="Nombre" variant="outlined" size="small"
								value={newTagName} onChange={(e) => setNewTagName(e.target.value)}
								style={{ flex: 1 }} />
							<TextField label="Color" type="color" variant="outlined" size="small"
								value={newTagColor} onChange={(e) => setNewTagColor(e.target.value)}
								style={{ width: 80 }}
								InputLabelProps={{ shrink: true }} />
							<Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddTag}>
								Agregar
							</Button>
						</Box>

						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Color</TableCell>
										<TableCell>Nombre</TableCell>
										<TableCell>Vista previa</TableCell>
										<TableCell align="center">Acciones</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{tags.map((tag) => (
										<TableRow key={tag.id} hover>
											{editingTag === tag.id ? (
												<>
													<TableCell>
														<TextField type="color" size="small" variant="outlined"
															value={editTagColor} onChange={(e) => setEditTagColor(e.target.value)}
															style={{ width: 60 }} />
													</TableCell>
													<TableCell>
														<TextField size="small" variant="outlined"
															value={editTagName} onChange={(e) => setEditTagName(e.target.value)}
															style={{ width: "100%" }} />
													</TableCell>
													<TableCell>
														<Chip label={editTagName || "Preview"} size="small"
															style={{ backgroundColor: editTagColor, color: "#fff" }} />
													</TableCell>
													<TableCell align="center">
														<IconButton size="small" onClick={handleUpdateTag} style={{ color: "#2e7d32" }}>
															<CheckIcon fontSize="small" />
														</IconButton>
														<IconButton size="small" onClick={() => setEditingTag(null)} style={{ color: "#d32f2f" }}>
															<CloseIcon fontSize="small" />
														</IconButton>
													</TableCell>
												</>
											) : (
												<>
													<TableCell>
														<Box style={{ width: 24, height: 24, borderRadius: 4, backgroundColor: tag.color }} />
													</TableCell>
													<TableCell>{tag.name}</TableCell>
													<TableCell>
														<Chip label={tag.name} size="small"
															style={{ backgroundColor: tag.color, color: "#fff" }} />
													</TableCell>
													<TableCell align="center">
														<IconButton size="small" onClick={() => {
															setEditingTag(tag.id);
															setEditTagName(tag.name);
															setEditTagColor(tag.color);
														}}>
															<EditIcon fontSize="small" />
														</IconButton>
														<IconButton size="small" onClick={() => {
															setDeletingTagId(tag.id);
															setDeleteTagConfirm(true);
														}}>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</TableCell>
												</>
											)}
										</TableRow>
									))}
									{tags.length === 0 && (
										<TableRow>
											<TableCell colSpan={4} align="center">No hay etiquetas configuradas</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>

					<Dialog open={deleteTagConfirm} onClose={() => setDeleteTagConfirm(false)}>
						<DialogTitle>Eliminar etiqueta</DialogTitle>
						<DialogContent>
							<DialogContentText>
								¿Esta seguro que desea eliminar esta etiqueta? Se removera de todos los chats que la tengan asignada.
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setDeleteTagConfirm(false)}>Cancelar</Button>
							<Button onClick={handleDeleteTag} color="secondary" variant="contained">Eliminar</Button>
						</DialogActions>
					</Dialog>
				</TabPanel>

				{/* Tab Categorias R.R. */}
				<TabPanel value={tab} index={7}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Categorias de Respuestas Rapidas</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Administre las categorias disponibles para organizar las respuestas rapidas.
						</Typography>

						<Box display="flex" style={{ gap: 12, marginBottom: 16 }} alignItems="center">
							<TextField label="Nueva categoria" variant="outlined" size="small"
								value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
								style={{ flex: 1 }}
								onKeyPress={(e) => { if (e.key === "Enter") addCategory(); }} />
							<Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={addCategory}>
								Agregar
							</Button>
						</Box>

						<List>
							{getCategories().map((cat, idx) => (
								<ListItem key={idx} divider>
									{editingCatIdx === idx ? (
										<>
											<TextField size="small" variant="outlined" value={editCatName}
												onChange={(e) => setEditCatName(e.target.value)}
												style={{ flex: 1, marginRight: 8 }} />
											<IconButton size="small" onClick={saveEditCategory} style={{ color: "#2e7d32" }}>
												<CheckIcon fontSize="small" />
											</IconButton>
											<IconButton size="small" onClick={() => setEditingCatIdx(null)} style={{ color: "#d32f2f" }}>
												<CloseIcon fontSize="small" />
											</IconButton>
										</>
									) : (
										<>
											<ListItemText primary={cat} />
											<ListItemSecondaryAction>
												<IconButton size="small" onClick={() => { setEditingCatIdx(idx); setEditCatName(cat); }}>
													<EditIcon fontSize="small" />
												</IconButton>
												<IconButton size="small" onClick={() => removeCategory(idx)}>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</ListItemSecondaryAction>
										</>
									)}
								</ListItem>
							))}
							{getCategories().length === 0 && (
								<ListItem>
									<ListItemText primary="No hay categorias configuradas" />
								</ListItem>
							)}
						</List>

						<Box mt={2}>
							<Button variant="contained" color="primary" startIcon={<SaveIcon />}
								onClick={() => saveMultiple(["quickAnswerCategories"])}>
								Guardar Categorias
							</Button>
						</Box>
					</Paper>
				</TabPanel>

				{/* Tab API */}
				<TabPanel value={tab} index={8}>
					<Paper className={classes.tabContent} elevation={2}>
						<Typography variant="h6" className={classes.sectionTitle}>Token API</Typography>
						<Typography variant="body2" color="textSecondary" className={classes.sectionDesc}>
							Use este token para integrar aplicaciones externas con la API de la plataforma.
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<TextField label="Token Api" variant="outlined" fullWidth InputProps={{ readOnly: true }}
									value={settings.length > 0 ? getSettingValue("userApiToken") : ""} />
							</Grid>
						</Grid>
					</Paper>
				</TabPanel>
			</Paper>
		</MainContainer>
	);
};

export default Settings;
